// @flow

import React from 'react';
import { zoomTableActionsConfig } from '../../commons/zoomTable/zoomTableConfig';
import { RenderRetailTableActionButtons } from '../../commons/tableActionButton/tableActionButton';

import {
  getRetailTableCellObject,
  getFormattedDate,
  getFormattedTime,
  getFormattedDateAndTime,
  goToCNTracking,
  paymentTypeMap
} from '../../../services/helpers';

const getWriteoffTicketId = writeOffTicketId =>
  `${writeOffTicketId ? `Ticket Id: ${writeOffTicketId}` : '--'}`;

const rpCollectionsTableColumns = [
  {
    Header: 'CN',
    headerClassName: 'flex-10',
    className: 'table-cell-custom-retail flex-10',
    accessor: 'cnote',
    Cell: row =>
      getRetailTableCellObject(
        row.original.cnote,
        row.original.paymentMode,
        row,
        goToCNTracking,
        'cn-highlight'
      ), // paymentMode and paymentType are interchanged due to interchanged values from backend
    sortMethod: (a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1)
  },
  {
    Header: 'Client Details',
    headerClassName: 'flex-15',
    className: 'table-cell-custom-retail flex-15',
    accessor: 'clientName',
    Cell: row =>
      getRetailTableCellObject(
        row.original.clientName,
        row.original.clientNumber,
        row
      ),
    sortMethod: (a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1)
  },
  {
    Header: 'Booking Time',
    headerClassName: 'flex-10',
    className: 'table-cell-custom-retail flex-10',
    accessor: 'createdAt',
    Cell: (row: Object) =>
      getRetailTableCellObject(
        getFormattedDate(row.original.createdAt),
        getFormattedTime(row.original.createdAt)
      ),
    sortMethod: (a, b) => (a > b ? 1 : -1)
  },
  {
    headerClassName: 'table-head-custom-retail flex-20',
    Header: () => (
      <div>
        <div>Amount</div>
        <div>Payment Mode</div>
      </div>
    ),
    className: 'table-cell-custom-retail flex-20',
    accessor: 'totalAmount',
    Cell: row =>
      getRetailTableCellObject(
        `₹${Math.round(row.original.totalRoundOffAmount * 100) / 100}`,
        `${row.original.paymentType}${
          row.original.paymentType !== paymentTypeMap.CASH.displayValue &&
          row.original.chequeNumber
            ? ` No. ${row.original.original.chequeNumber}`
            : ''
        } ${
          row.original.bankNameOfBouncedCheque &&
          row.original.chequeNumberOfBouncedCheque
            ? `, Bounced Cheque ${row.original.bankNameOfBouncedCheque}|${row.original.chequeNumberOfBouncedCheque}`
            : ''
        } `
      ),
    sortMethod: (a, b) => (a > b ? 1 : -1)
  },
  {
    headerClassName: 'table-head-custom-retail flex-10',
    Header: () => (
      <div>
        <div>TDS</div>
        <div>Deduction</div>
      </div>
    ),
    className: 'table-cell-custom-retail flex-10',
    accessor: 'tdsPercentage',
    Cell: (row: Object) =>
      getRetailTableCellObject(
        row.original.tdsPercentage &&
          row.original.tdsPercentage.toFixed(3).concat(' %')
      ),
    sortMethod: (a, b) => (a > b ? 1 : -1)
  },
  {
    headerClassName: 'table-head-custom-retail flex-15',
    Header: () => (
      <div>
        <div>Handover Status</div>
        <div>Transaction ID</div>
      </div>
    ),
    className: 'table-cell-custom-retail flex-15',
    accessor: 'handoverTransactionId',
    Cell: row =>
      getRetailTableCellObject(
        row.original.handoverStatus !== null
          ? row.original.handoverStatus
          : '-',
        row.original.handoverTransactionId !== null
          ? row.original.handoverTransactionId
          : '-'
      ),
    sortMethod: (a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1)
  },
  {
    Header: 'Handover Details',
    headerClassName: 'flex-15',
    className: 'table-cell-custom-retail flex-15',
    accessor: 'handoverRecieverName',
    Cell: (row: Object) =>
      row.original.handoverTransactionId !== null
        ? getRetailTableCellObject(
            row.original.handoverRecieverName
              ? row.original.handoverRecieverName
              : getWriteoffTicketId(row.original.writeOffTicketId),
            getFormattedDateAndTime(row.original.handoverTime)
          )
        : getRetailTableCellObject(
            getWriteoffTicketId(row.original.writeOffTicketId)
          ),
    sortMethod: (a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1)
  }
];

export const rpCollectionsCompletedTableConfig = {
  columns: rpCollectionsTableColumns,
  className: 'zoomTableLegacyWrapper',
  noDataText: 'No data found',
  minRows: 7,
  pageSizeOptions: [7, 10, 15, 25, 50, 100],
  style: { maxHeight: 'calc(100vh - 22rem)' }
};

export const rpCollectionsPendingTableConfig = {
  columns: [
    ...rpCollectionsTableColumns,
    {
      Header: 'Actions',
      headerClassName: 'flex-15',
      className: 'flex-15 rt-td table-cell-custom-retail',
      width: 150,
      maxWidth: 150,
      sortable: false,
      Cell: obj =>
        obj.original && obj.original.isKnockoffEligible ? (
          <RenderRetailTableActionButtons
            rowData={obj.original || {}}
            buttonConfig={rpCollectionsTableActionsConfig.buttons}
          />
        ) : obj.original && obj.original.paymentType === 'CHEQUE' ? (
          <div
            className="break-word"
            title="Please deposit cheque in nearest branch"
            style={{ color: 'red' }}
          >
            Please deposit cheque in nearest branch
          </div>
        ) : obj.original && obj.original.paymentType === 'CASH' ? (
          <div
            className="break-word"
            title="Knocking off can be done once CN is out of first OU"
            style={{ color: 'red' }}
          >
            Knocking off can be done once CN is out of first OU
          </div>
        ) : null
    }
  ],
  className: 'zoomTableLegacyWrapper',
  noDataText: 'No data found',
  minRows: 7,
  pageSizeOptions: [7, 10, 15, 25, 50, 100],
  style: { maxHeight: 'calc(100vh - 22rem)' }
};

export const rpCollectionsTableActionsConfig = {
  buttons: [
    {
      label: 'KNOCK OFF',
      hide: rowData => {
        return !rowData.isKnockoffEligible;
      },
      onClick: rowData => {
        if (
          zoomTableActionsConfig.RP_COLLECTION_HANDOVER_PENDING.actions &&
          zoomTableActionsConfig.RP_COLLECTION_HANDOVER_PENDING.actions.knockOff
        ) {
          zoomTableActionsConfig.RP_COLLECTION_HANDOVER_PENDING.actions.knockOff(
            rowData.id,
            rowData.cnote
          );
        }
      }
    }
  ]
};

const filterTableDataPaymentMode = (
  preFilteredData,
  paymentModeSelectedOption
) => {
  // null check for paymentModeSelectedOption
  if (!paymentModeSelectedOption) {
    return preFilteredData;
  }
  const searchString = paymentModeSelectedOption.toLowerCase().trim();

  let filteredArr;

  if (searchString) {
    filteredArr = preFilteredData.filter(
      row =>
        row.paymentType &&
        row.paymentType.toLowerCase().indexOf(searchString) >= 0
    );
  } else {
    filteredArr = preFilteredData;
  }

  return filteredArr;
};

export const filterTableData = (newState, filterObj) => {
  let filteredArr = newState;
  let paymentModefilteredArr;

  if (filterObj.paymentModeSelectedOption) {
    paymentModefilteredArr = filterTableDataPaymentMode(
      filteredArr,
      filterObj.paymentModeSelectedOption
    );
  } else {
    paymentModefilteredArr = filteredArr;
  }

  if (filterObj.searchString) {
    const searchString = filterObj.searchString.toLowerCase().trim();
    filteredArr = paymentModefilteredArr.filter(
      row =>
        (row.cnote && row.cnote.toLowerCase().indexOf(searchString) >= 0) ||
        (row.clientName &&
          row.clientName.toLowerCase().indexOf(searchString) >= 0) ||
        (row.clientNumber &&
          row.clientNumber.toLowerCase().indexOf(searchString) >= 0) ||
        (row.handoverTransactionId &&
          row.handoverTransactionId.toLowerCase().indexOf(searchString) >= 0) ||
        (row.handoverRecieverName &&
          row.handoverRecieverName.toLowerCase().indexOf(searchString) >= 0) ||
        (row.collectUserName &&
          row.collectUserName.toLowerCase().indexOf(searchString) >= 0) ||
        (row.collectUserMobile &&
          row.collectUserMobile.toLowerCase().indexOf(searchString) >= 0) ||
        (row.paymentType &&
          row.paymentType.toLowerCase().indexOf(searchString) >= 0) ||
        (row.bankName &&
          row.bankName.toLowerCase().indexOf(searchString) >= 0) ||
        (row.chequeNumber &&
          row.chequeNumber.toLowerCase().indexOf(searchString) >= 0)
    );
  } else {
    filteredArr = paymentModefilteredArr;
  }

  return filteredArr;
};
